import React from 'react';
import { Link } from 'react-router-dom';
import { FiFacebook, FiInstagram, FiYoutube, FiPhone, FiMail, FiMapPin, FiTwitter, FiMessageCircle } from 'react-icons/fi';
import { useSettings } from '../../context/SettingsContext';
import { getImageUrl } from '../../utils/helpers';

const Footer = () => {
  const { settings } = useSettings();

  return (
    <footer className="bg-primary-800 text-white mt-16">
      {/* Main footer */}
      <div className="container-custom py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              {settings.logo ? (
                <img src={getImageUrl(settings.logo)} alt={settings.siteName} className="h-10 w-auto object-contain brightness-0 invert" />
              ) : (
                <>
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow">
                    <span className="text-primary-700 font-bold text-xl">H</span>
                  </div>
                  <div>
                    <p className="text-xl font-extrabold text-white">{settings.siteName || 'Hospitex'}</p>
                    <p className="text-xs text-primary-300 leading-none">.com.pk</p>
                  </div>
                </>
              )}
            </div>
            <p className="text-primary-300 text-sm leading-relaxed mb-5">
              {settings.tagline || "Pakistan's leading online medical & hospital equipment supplier."}
            </p>
            <div className="flex gap-2">
              {settings.socialLinks?.facebook && (
                <a href={settings.socialLinks.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 bg-primary-700 hover:bg-primary-600 rounded-xl flex items-center justify-center transition-colors">
                  <FiFacebook size={15} />
                </a>
              )}
              {settings.socialLinks?.instagram && (
                <a href={settings.socialLinks.instagram} target="_blank" rel="noreferrer" className="w-9 h-9 bg-primary-700 hover:bg-primary-600 rounded-xl flex items-center justify-center transition-colors">
                  <FiInstagram size={15} />
                </a>
              )}
              {settings.socialLinks?.youtube && (
                <a href={settings.socialLinks.youtube} target="_blank" rel="noreferrer" className="w-9 h-9 bg-primary-700 hover:bg-primary-600 rounded-xl flex items-center justify-center transition-colors">
                  <FiYoutube size={15} />
                </a>
              )}
              {settings.socialLinks?.twitter && (
                <a href={settings.socialLinks.twitter} target="_blank" rel="noreferrer" className="w-9 h-9 bg-primary-700 hover:bg-primary-600 rounded-xl flex items-center justify-center transition-colors">
                  <FiTwitter size={15} />
                </a>
              )}
              {settings.whatsapp && (
                <a href={`https://wa.me/${settings.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="w-9 h-9 bg-green-600 hover:bg-green-500 rounded-xl flex items-center justify-center transition-colors">
                  <FiMessageCircle size={15} />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-base mb-5 text-white">Quick Links</h4>
            <ul className="space-y-2.5">
              {[
                { to: '/', label: 'Home' },
                { to: '/shop', label: 'Shop All Products' },
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact Us' },
                { to: '/account/orders', label: 'Track My Order' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="text-primary-300 hover:text-white text-sm transition-colors flex items-center gap-1.5 group">
                    <span className="w-1 h-1 bg-primary-500 group-hover:bg-accent-400 rounded-full transition-colors" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-bold text-base mb-5 text-white">Product Categories</h4>
            <ul className="space-y-2.5">
              {['Hospital Furniture', 'Electromedical', 'Rehabilitation', 'Home Healthcare', 'Surgical Instruments', 'Physiotherapy'].map((cat) => (
                <li key={cat}>
                  <Link to={`/shop?search=${encodeURIComponent(cat)}`} className="text-primary-300 hover:text-white text-sm transition-colors flex items-center gap-1.5 group">
                    <span className="w-1 h-1 bg-primary-500 group-hover:bg-accent-400 rounded-full transition-colors" />
                    {cat}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-base mb-5 text-white">Contact Us</h4>
            <ul className="space-y-3.5">
              {settings.phone && (
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-700 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FiPhone size={14} className="text-primary-300" />
                  </div>
                  <div>
                    <p className="text-xs text-primary-400 mb-0.5">Phone</p>
                    <a href={`tel:${settings.phone}`} className="text-sm text-primary-200 hover:text-white transition-colors">{settings.phone}</a>
                    {settings.phone2 && <p className="text-sm text-primary-200">{settings.phone2}</p>}
                  </div>
                </li>
              )}
              {settings.email && (
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-700 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FiMail size={14} className="text-primary-300" />
                  </div>
                  <div>
                    <p className="text-xs text-primary-400 mb-0.5">Email</p>
                    <a href={`mailto:${settings.email}`} className="text-sm text-primary-200 hover:text-white transition-colors break-all">{settings.email}</a>
                  </div>
                </li>
              )}
              {settings.address && (
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-700 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FiMapPin size={14} className="text-primary-300" />
                  </div>
                  <div>
                    <p className="text-xs text-primary-400 mb-0.5">Address</p>
                    <p className="text-sm text-primary-200">{settings.address}</p>
                    {settings.city && <p className="text-sm text-primary-200">{settings.city}, {settings.country}</p>}
                  </div>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-primary-700">
        <div className="container-custom py-4 flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="text-primary-400 text-xs">&copy; {new Date().getFullYear()} {settings.siteName || 'Hospitex'}.com.pk — All rights reserved.</p>
          <div className="flex gap-4 text-xs text-primary-400">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
